import { ReactQueryStaleTime } from '@openx/constants/react-query.ts';
import type { CollectionFormat } from '@openx/types/collectionFormat.ts';
import type { AudiencePricing } from '@openx/types/targeting/audience.ts';
import type { UseGetAudienceMultiproviderInfo } from '@openx/types/targeting/targetingHookTypes.ts';
import '@openx/utils/api/axiosRequestConfigAugmentation.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

type AudienceData = {
  is_multi_partner?: boolean | null;
  pricing?: AudiencePricing | null;
};

async function fetchAudience(audienceId: string): Promise<AudienceData | null> {
  if (!audienceId) return null;

  try {
    const { data } = await axios.get<CollectionFormat<AudienceData>>('audience', {
      params: { id: audienceId, fields: 'is_multi_partner,pricing' },
      skipGlobalForbiddenHandling: true,
    });

    return data?.objects?.[0] ?? null;
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    if (status === 403 || status === 404) {
      return null;
    }

    throw error;
  }
}

export const useGetAudienceMultiproviderInfo: UseGetAudienceMultiproviderInfo = ({
  selectedAudienceOption,
  values,
}) => {
  const { t } = useTranslation();
  const audienceId = values?.[0] ?? '';
  const skipQuery = !selectedAudienceOption || selectedAudienceOption !== 'openaudience_custom' || !audienceId;

  const cleanAudienceId = audienceId?.replace('openaudience-', '');

  const { data: audience, isLoading: loadingAudienceCheck } = useQuery<AudienceData | null>({
    enabled: !skipQuery && !!cleanAudienceId,
    meta: {
      errorMessage: t('Failed to fetch audience with id: {audienceId}', { audienceId: cleanAudienceId }),
    },
    queryFn: () => fetchAudience(cleanAudienceId),
    queryKey: [QueryKeys.AUDIENCE, cleanAudienceId],
    staleTime: ReactQueryStaleTime.MINUTES_15,
  });

  const hasMultiProviderPricingData = !!audience?.is_multi_partner;

  return {
    hasMultiProviderPricingData,
    pricingInfo: audience?.pricing as AudiencePricing,
    loadingAudienceCheck,
  };
};
